<template>
  <div class="cart-order">
    <!--Order Seller-->
    <header class="cart-order__header">
      <CheckoutSeller
        :seller="order.seller"
        :shipment-city="order.shipment_city"
      />
      <template v-if="hasCashback">
        <CartCashback
          :cashback="order.cashback"
          :is-authenticated="isAuthenticated"
        />
      </template>
    </header>
    <!---->

    <!--Order List-->
    <div class="cart-order__content">
      <CartItem
        v-for="cartItem in order.items"
        :key="`cart-offer-${cartItem.uuid}`"
        :cart-item="cartItem"
        :order-id="order.uuid"
        :updating="checkUpdating(cartItem.uuid)"
        @close-modal="closeModal"
        @input-is-updating="handleCounterUpdate"
      />
    </div>
    <!---->

    <!--Order about and Submit-->
    <div v-if="!allCartItemsRemoved" class="cart-order__footer">
      <div class="cart-order__footer-content">
        <div v-if="isUpdating" class="loading-overlay" />

        <!--Price Order-->
        <div class="cart-order__total">
          <span>{{ $t('cart.total') }}:</span>
          <span
            class="cart-order__total-price"
            :class="{ 'cart-order__total-price--update': isUpdating }"
          >
            {{ getFormattedPrice(getOrderTotal) }} <span class="icon-hryvnia" />
          </span>
        </div>
        <!---->

        <!--Submit Order-->
        <BaseButton
          id="cart-button-order-submit"
          class="cart-order__submit"
          theme="accent"
          :disabled="hasQuantityConflict || !allCartItemsAvailable"
          :loading="checkoutFetching"
          @click="emit('on-get-checkout', order)"
        >
          {{ checkoutMode ? $t('checkout.submit.continue_ordering') : $t('cart.proceed_to_checkout') }}
        </BaseButton>
        <!---->
      </div>
    </div>
    <!---->
  </div>
</template>

<script lang="ts" setup>
import { modalCartId } from '@/constants/modalNames'
import type { CartOrder } from '~/types/checkout'

type Props = {
  order: CartOrder
  checkoutFetching: boolean
  checkoutMode?: boolean
}
type Emits = {
  (eventName: 'on-get-checkout', value: CartOrder): void
}

const props = withDefaults(defineProps<Props>(), {
  checkoutMode: false
})
const emit = defineEmits<Emits>()
const authUserStore = useAuthUserStore()
const { isAuthenticated } = storeToRefs(authUserStore)
const cartStore = useCartStore()
const { $vfm } = useNuxtApp()

const { updatingOffers } = storeToRefs(cartStore)

const counterIsUpdated = ref<boolean>(false)

// Computed
const hasCashback = computed<boolean>(() => {
  return props.order.cashback > 0
})

const isUpdating = computed<boolean>(() => {
  if (counterIsUpdated.value) {
    return counterIsUpdated.value
  }

  if (updatingOffers.value.length === 0) {
    return false
  }

  return updatingOffers.value.some((offerUUid) => {
    return props.order.items.some((offer) => offer.uuid === offerUUid)
  })
})

const allCartItemsAvailable = computed(() => {
  return props.order.items.every((orderItem) => {
    return orderItem.additionalInfo.isAvailable
  })
})

// всі офери мають статус видалені
const allCartItemsRemoved = computed<boolean>(() => {
  return props.order.items.every((offer) => offer.additionalInfo.isTempRemoved)
})

const hasQuantityConflict = computed<boolean>(() => {
  return props.order.items.some((cartItem) => cartItem.quantity > cartItem.quantity_max)
})

const getOrderTotal = computed(() => {
  return props.order.items.reduce((total: number, cartItem) => {
    if (cartItem.additionalInfo.isTempRemoved) {
      return total
    }

    return total + (cartItem.price_unit * cartItem.quantity)
  }, 0)
})

// Methods
const checkUpdating = (offerId: string) => {
  return updatingOffers.value.includes(offerId)
}

const closeModal = () => {
  $vfm.close(modalCartId)
}

const handleCounterUpdate = (isUpdate: boolean) => {
  counterIsUpdated.value = isUpdate
}
</script>
